// Error reporting, abstracted away from @sentry/react.
//
// The core code shared with the Expo app (and eventually the SDK) must not
// import a browser Sentry SDK directly: the web app uses @sentry/react, native
// needs @sentry/react-native, and a partner embedding the SDK may want their own
// reporter or none at all.

/** Free-form structured context attached to a report. Mirrors Sentry's shape. */
export interface ErrorReportContext {
  tags?: Record<string, string | number | boolean | undefined>
  extra?: Record<string, unknown>
  level?: 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug'
}

export interface Breadcrumb {
  category?: string
  message?: string
  level?: ErrorReportContext['level']
  data?: Record<string, unknown>
}

export interface ErrorReporter {
  captureException(error: unknown, context?: ErrorReportContext): void
  captureMessage(message: string, context?: ErrorReportContext): void
  addBreadcrumb(breadcrumb: Breadcrumb): void
}

/**
 * Used until the host app installs a real reporter. It logs rather than
 * swallowing, so a missing setErrorReporter() call degrades to console noise
 * instead of silently losing errors.
 */
export const consoleErrorReporter: ErrorReporter = {
  captureException(error, context) {
    console.error('[errorReporter] captureException', error, context ?? '')
  },
  captureMessage(message, context) {
    console.warn('[errorReporter] captureMessage', message, context ?? '')
  },
  addBreadcrumb(breadcrumb) {
    console.debug('[errorReporter] breadcrumb', breadcrumb)
  },
}

let current: ErrorReporter = consoleErrorReporter

/**
 * Installs the host application's reporter. Call once during startup, before
 * any core code runs.
 *
 * This is a module-level registry rather than a constructor parameter on
 * purpose. The reporting call sites are leaf utilities several layers below any
 * seam that could plausibly own the dependency -- `hashFileSha1`,
 * `getOrCreateSettingsForUser`, a Redux middleware -- so threading a reporter
 * through them would mean changing every intermediate caller's signature. That
 * is a large, risky diff for a phase whose whole point is to change no behavior.
 * Constructor injection stays the pattern for dependencies with a natural owner
 * (see BackendClient on SyncActor).
 */
export function setErrorReporter(reporter: ErrorReporter): void {
  current = reporter
}

export function getErrorReporter(): ErrorReporter {
  return current
}

/** Restores the console default. Intended for test teardown. */
export function resetErrorReporter(): void {
  current = consoleErrorReporter
}
